import React from "react";
import JudgmentSeal from "@/components/judgment/JudgmentSeal";
import JudgmentWarningBanner from "@/components/judgment/JudgmentWarningBanner";
import { fmtMoney } from "@/lib/chart-utils";

/**
 * Solemn judicial-style rendering of the simulated judgment.
 * - Merriweather serif for the document body
 * - Numbered roman sections (I, II, III, IV, V, VI)
 * - Permanent pedagogical warning embedded twice (top + bottom)
 */

const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII"];

function Section({ index, title, children, testId }) {
  return (
    <section className="mt-8" data-testid={testId}>
      <h3 className="font-judicial text-xl font-bold uppercase tracking-wide text-[#1A3C5E] dark:text-brand-gold">
        <span className="mr-3 font-black">{ROMAN[index]}.</span>
        {title}
      </h3>
      <div className="mt-3 font-judicial text-[15px] leading-[1.75] text-foreground/95">
        {children}
      </div>
    </section>
  );
}

function formatFrDate(isoLike) {
  if (!isoLike) return "";
  try {
    const d = new Date(isoLike);
    if (Number.isNaN(d.getTime())) return String(isoLike);
    return d.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return String(isoLike);
  }
}

export default function JudgmentDocument({ judgment, currency = "UM" }) {
  if (!judgment) return null;
  const faits = judgment.expose_des_faits || {};
  const dispositif = judgment.dispositif || {};
  const qualifications = judgment.qualification_juridique || [];
  const articles = judgment.articles_appliques || [];

  return (
    <article
      data-testid="judgment-document"
      className="relative overflow-hidden rounded-lg border-2 border-[#1A3C5E]/30 bg-[#FDFBF4] px-6 py-8 shadow-inner dark:border-brand-gold/30 dark:bg-[#1a1a1a] sm:px-10 sm:py-10"
    >
      {/* Watermark */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-[0.055] dark:opacity-[0.09]"
      >
        <div className="-rotate-12 text-[88px] font-black tracking-widest text-[#C0392B] sm:text-[128px]">
          PÉDAGOGIQUE
        </div>
      </div>

      <div className="relative">
        {/* Head — seal + tribunal */}
        <header className="flex flex-col items-center gap-4 text-center">
          <JudgmentSeal size={120} />
          <div>
            <div
              className="font-judicial text-2xl font-black uppercase tracking-widest text-[#1A3C5E] dark:text-brand-gold"
              data-testid="judgment-tribunal"
            >
              {judgment.tribunal}
            </div>
            <div className="mt-2 font-judicial text-sm italic text-muted-foreground">
              Simulation du {formatFrDate(judgment.date_simulation)}
            </div>
          </div>
          <div
            className="mt-2 border-y-2 border-[#1A3C5E]/40 px-6 py-2 font-judicial text-lg font-bold uppercase tracking-[0.25em] text-[#1A3C5E] dark:border-brand-gold/40 dark:text-brand-gold"
            data-testid="judgment-header"
          >
            {judgment.header}
          </div>
        </header>

        {/* Top warning */}
        <div className="mt-6">
          <JudgmentWarningBanner compact />
        </div>

        {/* Sections */}
        <Section index={0} title="Exposé des faits" testId="judgment-section-expose">
          <div className="space-y-4">
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-widest text-muted-foreground">
                Volet financier
              </div>
              <p className="whitespace-pre-line" data-testid="judgment-faits-financier">
                {faits.financier || "—"}
              </p>
            </div>
            <div>
              <div className="mb-1 text-[11px] uppercase tracking-widest text-muted-foreground">
                Volet vitaliste (traduction en vies humaines)
              </div>
              <p
                className="whitespace-pre-line font-semibold"
                data-testid="judgment-faits-vitaliste"
              >
                {faits.vitaliste || "—"}
              </p>
            </div>
          </div>
        </Section>

        <Section
          index={1}
          title="Qualification juridique"
          testId="judgment-section-qualification"
        >
          {qualifications.length ? (
            <ol className="list-decimal space-y-2 pl-6">
              {qualifications.map((q, i) => (
                <li key={i} data-testid={`judgment-qualif-${i}`}>
                  <span className="font-bold">{q.qualification}</span>
                  {q.fondement && (
                    <span className="text-muted-foreground"> — {q.fondement}</span>
                  )}
                  {q.jours_de_vie != null && (
                    <span className="ml-2 rounded bg-[#C0392B]/15 px-1.5 py-0.5 font-mono text-xs font-bold text-[#C0392B] dark:bg-[#C0392B]/30 dark:text-rose-200">
                      {q.jours_de_vie} j. de vie
                    </span>
                  )}
                </li>
              ))}
            </ol>
          ) : (
            <p className="italic text-muted-foreground">
              Aucune qualification retenue par la juridiction fictive.
            </p>
          )}
        </Section>

        <Section index={2} title="Motifs vitalistes" testId="judgment-section-motifs">
          <p
            className="whitespace-pre-line italic"
            data-testid="judgment-motifs"
          >
            {judgment.motifs_vitalistes || "—"}
          </p>
        </Section>

        <Section
          index={3}
          title="Par ces motifs — Dispositif"
          testId="judgment-section-dispositif"
        >
          <div className="space-y-3">
            <div
              className="rounded-md border-l-4 border-[#C0392B] bg-[#C0392B]/5 p-3 dark:bg-[#C0392B]/15"
              data-testid="judgment-dispo-penales"
            >
              <div className="text-[11px] uppercase tracking-widest text-[#C0392B]">
                Sanctions pénales
              </div>
              <div className="mt-1 whitespace-pre-line">
                {dispositif.sanctions_penales || "—"}
              </div>
            </div>

            <div
              className="rounded-md border-l-4 border-[#1A3C5E] bg-[#1A3C5E]/5 p-3 dark:bg-[#1A3C5E]/30"
              data-testid="judgment-dispo-civile"
            >
              <div className="text-[11px] uppercase tracking-widest text-[#1A3C5E] dark:text-brand-gold">
                Réparation civile
              </div>
              <div className="mt-1 font-mono text-base font-bold">
                {dispositif.reparation_civile_en_UM != null
                  ? fmtMoney(dispositif.reparation_civile_en_UM, currency)
                  : "—"}
              </div>
            </div>

            <div
              className="rounded-md border-l-4 border-[#C9A84C] bg-[#C9A84C]/10 p-3"
              data-testid="judgment-dispo-vitaliste"
            >
              <div className="text-[11px] uppercase tracking-widest text-[#C9A84C]">
                Réparation vitaliste
              </div>
              <div className="mt-1 whitespace-pre-line font-bold">
                {dispositif.reparation_vitaliste || "—"}
              </div>
            </div>

            <div
              className="rounded-md border-l-4 border-muted-foreground bg-muted/30 p-3"
              data-testid="judgment-dispo-admin"
            >
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                Sanctions administratives
              </div>
              <div className="mt-1 whitespace-pre-line">
                {dispositif.sanctions_administratives || "—"}
              </div>
            </div>
          </div>
        </Section>

        <Section
          index={4}
          title="Articles appliqués"
          testId="judgment-section-articles"
        >
          {articles.length ? (
            <ul className="space-y-1.5 font-mono text-sm">
              {articles.map((a, i) => (
                <li key={i} data-testid={`judgment-article-${i}`}>
                  <strong>{a.article}</strong> — {a.texte_titre}
                  {a.portee && (
                    <span className="text-muted-foreground"> · {a.portee}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="italic text-muted-foreground">
              Aucun article mobilisé.
            </p>
          )}
        </Section>

        {/* Citation */}
        {judgment.citation_finale && (
          <blockquote
            className="mt-10 border-l-4 border-[#C9A84C] pl-5 font-judicial italic text-foreground/80"
            data-testid="judgment-citation"
          >
            « {judgment.citation_finale} »
            <div className="mt-2 text-xs not-italic text-muted-foreground">
              — d'après <em>La Nouvelle Théorie des Finances Publiques</em>,
              ELY Mustapha
            </div>
          </blockquote>
        )}

        {/* Mandatory bottom disclaimer */}
        <div className="mt-10">
          <JudgmentWarningBanner />
        </div>

        <div
          className="mt-5 text-center font-judicial text-xs italic text-muted-foreground"
          data-testid="judgment-mention"
        >
          {judgment.mention_legale}
        </div>
      </div>
    </article>
  );
}
